// Chantier : Boilerplate lot 4 (PLAN/audits/audit-boilerplate/BACKLOG.md)
// Regle metier : -
// Decision : -
// Labels/styles du module CAP/dechets, extraits de lib/labels.ts
// (decontamination du socle, Axe 1).

import type { GroupeDechet, IntegriteDechet, RegimeDechet, TypeDechet } from '@/services/referentiels';
import type { StatutCap } from '@/services/cap';

// Libelles accentues, ecran et PDF (FID dechet et fiche chantier depuis RC3.01).
export const INTEGRITE_LABELS: Record<IntegriteDechet, string> = {
  integre: 'Intègre',
  non_integre: 'Non intègre',
};

export const REGIME_LABELS: Record<RegimeDechet, string> = {
  bsda: 'BSDA',
  bsdd: 'BSDD',
  inerte: 'Inerte',
};

// Libelles en contexte groupe : affiches sous un en-tete de famille qui porte deja
// « Amiante » / « Plomb » (parcours client, cf. FAMILLE_GROUPES).
export const GROUPE_LABELS: Record<GroupeDechet, string> = {
  amiante_UN2590: 'UN 2590',
  amiante_UN2212: 'UN 2212',
  plomb: 'Plomb',
  fcr: 'FCR',
  autre: 'Autre',
};

// Libelles autonomes : pour les listes PLATES, sans en-tete de famille (referentiel
// admin). Seuls les deux groupes amiante different — ils portent leur famille, sinon
// « UN 2590 » seul n'identifie plus le dechet. A ne pas utiliser sous un
// SelectGroup/SelectLabel de famille, qui donne deja le contexte.
export const GROUPE_LABELS_AUTONOMES: Record<GroupeDechet, string> = {
  ...GROUPE_LABELS,
  amiante_UN2590: 'Amiante UN 2590',
  amiante_UN2212: 'Amiante UN 2212',
};

// Options de select derivees des dictionnaires ci-dessus, sur le modele de
// CATEGORIES_CAMION_OPTIONS (transportsLabels.ts) : toute liste proposee a
// l'utilisateur part d'ici, jamais d'un tableau redige a la main.
export const GROUPE_OPTIONS_AUTONOMES: { value: GroupeDechet; label: string }[] =
  (Object.entries(GROUPE_LABELS_AUTONOMES) as [GroupeDechet, string][])
    .map(([value, label]) => ({ value, label }));

export const REGIME_OPTIONS: { value: RegimeDechet; label: string }[] =
  (Object.entries(REGIME_LABELS) as [RegimeDechet, string][])
    .map(([value, label]) => ({ value, label }));

// RC3.74 / RC3.83 (D-UX-PILOTAGE-ADMIN) : « Nom (UN 2590, Famille 9, Non intègre, 170605*) », ordre de Mehdi.
// Groupe passe seulement par la sidebar de qualification (pas sur la fiche centre). Element absent omis,
// groupe « Autre » omis ; sans aucun element, le nom seul.
export function libelleTypeDechet(
  nom: string,
  type?: Pick<TypeDechet, 'famille' | 'integrite' | 'codeCed'> | null,
  groupe?: GroupeDechet | null,
): string {
  const infos = precisionsTypeDechet(type, groupe);
  return infos ? `${nom} (${infos})` : nom;
}

// Ce qui suit le nom entre parentheses dans libelleTypeDechet (RC4.13 : 2e ligne de la fiche centre).
export function precisionsTypeDechet(
  type?: Pick<TypeDechet, 'famille' | 'integrite' | 'codeCed'> | null,
  groupe?: GroupeDechet | null,
): string {
  return [
    groupe && groupe !== 'autre' ? GROUPE_LABELS[groupe] : '',
    type?.famille != null ? `Famille ${type.famille}` : '',
    type?.integrite ? INTEGRITE_LABELS[type.integrite] : '',
    type?.codeCed ?? '',
  ].filter(Boolean).join(', ');
}

export type FamilleDechet = 'Amiante' | 'Plomb' | 'FCR' | 'Autres';

export const FAMILLE_GROUPES: Record<FamilleDechet, GroupeDechet[]> = {
  'Amiante': ['amiante_UN2590', 'amiante_UN2212'],
  'Plomb': ['plomb'],
  'FCR': ['fcr'],
  'Autres': ['autre'],
};

export const STATUT_CAP_LABELS: Record<StatutCap, string> = {
  a_qualifier: 'À qualifier',
  qualifie: 'Qualifié',
  cap_demande: 'CAP demandé',
  pret: 'Prêt',
  annule: 'Annulé',
};

// Statut CAP simplifie pour la vue client : on aplatit les 5 statuts admin en 3.
// Source canonique unique pour eviter le drift de litteraux dans les consommateurs
// (cf. bug couleur badge 2026-05-06).
export type StatutCapClient = 'Prêt' | 'En préparation' | 'Annulé';

export const STATUT_CAP_LABELS_CLIENT: Record<StatutCap, StatutCapClient> = {
  pret: 'Prêt',
  a_qualifier: 'En préparation',
  qualifie: 'En préparation',
  cap_demande: 'En préparation',
  annule: 'Annulé',
};

// Pastille des statuts admin dans la grille Dechets de /operations. Pas de style admin plus complet : le
// style client ci-dessous regroupe les 5 statuts en 3. Pret = vert du badge client.
export const STATUT_CAP_STYLE: Record<StatutCap, { dot: string }> = {
  a_qualifier: { dot: 'bg-amber-500' },
  qualifie: { dot: 'bg-blue-500' },
  cap_demande: { dot: 'bg-violet-500' },
  pret: { dot: 'bg-green-500' },
  annule: { dot: 'bg-red-500' },
};

export const STATUT_CAP_CLIENT_STYLE: Record<StatutCapClient, { badge: string; border: string }> = {
  'En préparation': { badge: 'bg-amber-500 hover:bg-amber-500 text-white', border: 'border-l-amber-500' },
  'Prêt':           { badge: 'bg-green-500 hover:bg-green-500 text-white', border: 'border-l-green-500' },
  'Annulé':         { badge: 'bg-red-500 hover:bg-red-500 text-white',     border: 'border-l-red-500' },
};
